import { Component } from '@theme/component';

/**
 * @typedef {Object} SfFlashSaleBarRefs
 * @property {HTMLElement} [daysUnit]
 * @property {HTMLElement} [days]
 * @property {HTMLElement} [hours]
 * @property {HTMLElement} [minutes]
 * @property {HTMLElement} [seconds]
 */

const SECOND = 1000;
const MINUTE = SECOND * 60;
const HOUR = MINUTE * 60;
const DAY = HOUR * 24;

/** Read by sf-header.js and by `.sticky-content` so both sit below the bar. */
const HEIGHT_PROPERTY = '--sf-flash-bar-height';

/**
 * @param {number} value
 * @returns {string}
 */
const pad = (value) => String(value).padStart(2, '0');

/**
 * Sitewide flash sale bar: pinned to the top of the viewport, counting down to
 * the sale's end.
 *
 * The deadline arrives as an ISO string carrying the store's UTC offset, so the
 * instant is absolute and every visitor sees the same remaining time whatever
 * their own timezone. The countdown math mirrors sf-sweeps-countdown.js.
 *
 * The bar publishes its height as --sf-flash-bar-height so the sticky header
 * and announcement bar stack underneath it instead of overlapping it. When the
 * sale ends the bar hides itself and the height drops to 0, so everything
 * below slides back up.
 *
 * @extends {Component<SfFlashSaleBarRefs>}
 */
class SfFlashSaleBar extends Component {
  /** @type {number | null} */
  #deadline = null;
  /** @type {ReturnType<typeof setInterval> | null} */
  #interval = null;

  /** @type {ResizeObserver} */
  #resizeObserver = new ResizeObserver(([entry]) => {
    const height = entry?.borderBoxSize[0] ? Math.round(entry.borderBoxSize[0].blockSize) : 0;
    document.documentElement.style.setProperty(HEIGHT_PROPERTY, `${height}px`);
  });

  connectedCallback() {
    super.connectedCallback();

    const deadline = this.#readDeadline();

    // An unparseable date leaves nothing worth showing: drop the bar rather
    // than tick away at NaN.
    if (deadline == null || deadline <= Date.now()) {
      this.#end();
      return;
    }

    this.#deadline = deadline;
    this.#flattenWrappers();
    this.#resizeObserver.observe(this);
    this.#tick();
    this.#interval = setInterval(() => this.#tick(), SECOND);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#stop();
    this.#resizeObserver.disconnect();
    document.documentElement.style.removeProperty(HEIGHT_PROPERTY);
  }

  /**
   * position:sticky only holds within the parent box. Shopify wraps the bar in
   * a section element inside #header-group, both exactly as tall as the bar, so
   * both become display:contents and the bar sticks against <body> instead.
   * sf-header.js flattens the same wrappers for the sticky header.
   */
  #flattenWrappers() {
    const section = this.closest('.shopify-section');
    if (!(section instanceof HTMLElement)) return;

    section.style.display = 'contents';

    const headerGroup = section.parentElement;
    if (headerGroup?.id === 'header-group') {
      headerGroup.style.display = 'contents';
    }
  }

  /**
   * Reads the deadline from the JSON block the section renders.
   *
   * @returns {number | null} Epoch milliseconds, or null when absent or invalid.
   */
  #readDeadline() {
    const script = this.querySelector(':scope > script[type="application/json"]');

    if (!script?.textContent) return null;

    try {
      const { deadline } = JSON.parse(script.textContent);
      const parsed = Date.parse(deadline);

      return Number.isNaN(parsed) ? null : parsed;
    } catch {
      return null;
    }
  }

  #stop() {
    if (this.#interval == null) return;

    clearInterval(this.#interval);
    this.#interval = null;
  }

  #end() {
    this.#stop();
    this.hidden = true;
    document.documentElement.style.setProperty(HEIGHT_PROPERTY, '0px');
  }

  #tick() {
    if (this.#deadline == null) return;

    const remaining = this.#deadline - Date.now();

    if (remaining <= 0) {
      this.#end();
      return;
    }

    const { daysUnit, days, hours, minutes, seconds } = this.refs;
    const dayCount = Math.floor(remaining / DAY);

    // Days drop out on the final day so the bar reads "05h 12m 09s", not "0d …".
    if (daysUnit instanceof HTMLElement) daysUnit.hidden = dayCount === 0;
    if (days instanceof HTMLElement) days.textContent = String(dayCount);
    if (hours instanceof HTMLElement) hours.textContent = pad(Math.floor(remaining / HOUR) % 24);
    if (minutes instanceof HTMLElement) minutes.textContent = pad(Math.floor(remaining / MINUTE) % 60);
    if (seconds instanceof HTMLElement) seconds.textContent = pad(Math.floor(remaining / SECOND) % 60);
  }
}

if (!customElements.get('sf-flash-sale-bar')) {
  customElements.define('sf-flash-sale-bar', SfFlashSaleBar);
}
